import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { GraduationCap, Plus, Calendar, CurrencyDollar, Clock, UsersThree, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const thumbnails = [
  "https://static.prod-images.emergentagent.com/jobs/8f8a1e34-b7b5-4fc0-b7ca-0a56778fb2cb/images/d4b8ffef887e197514a47fb2cd962b1a02d68cf2bf3b5466179d8609bf49d9d5.png",
  "https://static.prod-images.emergentagent.com/jobs/8f8a1e34-b7b5-4fc0-b7ca-0a56778fb2cb/images/b27ca7caff0b63e0d8ae467ae6050379e9a9fc8099d017bb975b8953d134a745.png",
];

export default function ProgrammesPage() {
  const { user } = useAuth();
  const [programmes, setProgrammes] = useState([]);
  const [catalogueProgrammes, setCatalogueProgrammes] = useState([]);
  const [catalogueSelection, setCatalogueSelection] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", duration: "", fee: 0, min_payment_percent: 50, start_date: "", end_date: "", cohort: "", status: "active", thumbnail: "" });
  const [saving, setSaving] = useState(false);
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const [canCreate, setCanCreate] = useState(isAdmin);

  useEffect(() => {
    if (!isAdmin && user) {
      api.get("/auth/me/permissions")
        .then(r => setCanCreate((r.data?.permissions || []).includes("create_programmes")))
        .catch(() => {});
    }
  }, [user, isAdmin]);

  const fetchProgrammes = () => {
    api.get("/programmes").then(r => { setProgrammes(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchProgrammes(); }, []);

  // Load catalogue for the "Select from Catalogue" dropdown in the Create dialog.
  // Use /public-programmes so admins only see real catalogue entries (not other
  // LMS instances they may have created earlier).
  useEffect(() => {
    api.get("/public-programmes")
      .then(r => setCatalogueProgrammes(r.data || []))
      .catch(() => {});
  }, []);

  // When admin picks a catalogue entry, pre-fill the form. Cohort-specific
  // values (start/end dates, cohort label) are intentionally left blank.
  const applyCatalogueSelection = (id) => {
    setCatalogueSelection(id);
    if (!id) return;
    const p = catalogueProgrammes.find(x => x.id === id);
    if (!p) return;
    setForm(f => ({
      ...f,
      title: p.title || "",
      description: p.description || p.overview || "",
      duration: p.duration || "",
      fee: p.price_usd ?? p.fee ?? 0,
      status: p.status || "active",
      ideal_for: typeof p.ideal_for === "string" ? p.ideal_for : Array.isArray(p.ideal_for) ? p.ideal_for.join(", ") : "",
    }));
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      // Auto-derive enrollment_open from status so admins don't need a 2nd toggle.
      // active / enrollment_open ⇒ true; everything else ⇒ false.
      const statusLc = (form.status || "").toLowerCase();
      const enrollment_open = ["active", "enrollment_open", "running"].includes(statusLc);
      await api.post("/programmes", {
        ...form,
        fee: parseFloat(form.fee) || 0,
        min_payment_percent: parseFloat(form.min_payment_percent) || 50,
        enrollment_open,
      });
      toast.success("Programme created successfully");
      setDialogOpen(false);
      setCatalogueSelection("");
      setForm({ title: "", description: "", duration: "", fee: 0, min_payment_percent: 50, start_date: "", end_date: "", cohort: "", status: "active", thumbnail: "" });
      fetchProgrammes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create programme");
    }
    setSaving(false);
  };

  const handleDeleteProgramme = async (progId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this programme? This cannot be undone.")) return;
    try {
      await api.delete(`/programmes/${progId}`);
      toast.success("Programme deleted");
      setProgrammes(prev => prev.filter(p => p.id !== progId));
    } catch (err) { toast.error("Failed to delete programme"); }
  };


  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="programmes-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Programmes</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {canCreate ? "Programme Management" : "Available Programmes"}
            </h1>
          </div>
          {canCreate && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="create-programme-btn">
                  <Plus size={16} weight="bold" /> New Programme
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg rounded-none">
                <DialogHeader>
                  <DialogTitle className="text-xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Create Programme</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
                  <div className="bg-amber-50 border border-amber-200 px-3 py-2">
                    <Label className="text-xs font-bold uppercase tracking-widest text-amber-700">Select from Catalogue (optional)</Label>
                    <Select value={catalogueSelection || "__scratch"} onValueChange={(v) => applyCatalogueSelection(v === "__scratch" ? "" : v)}>
                      <SelectTrigger className="mt-1 rounded-none bg-white" data-testid="catalogue-programme-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__scratch">— Create from scratch —</SelectItem>
                        {catalogueProgrammes.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {`${p.code ? p.code + " · " : ""}${p.title}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-amber-700 mt-1">Picking a catalogue programme pre-fills the form. Cohort-specific fields (dates, cohort label) stay blank for you to set.</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Title</Label>
                    <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 rounded-none" data-testid="programme-title-input" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Description</Label>
                    <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 rounded-none" rows={3} data-testid="programme-desc-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Duration</Label>
                      <Input value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} placeholder="e.g. 6 months" className="mt-1 rounded-none" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Cohort</Label>
                      <Input value={form.cohort} onChange={e => setForm({...form, cohort: e.target.value})} placeholder="e.g. Cohort 2026A" className="mt-1 rounded-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Fee (USD)</Label>
                      <Input type="number" value={form.fee} onChange={e => setForm({...form, fee: e.target.value})} className="mt-1 rounded-none" data-testid="programme-fee-input" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Min Payment %</Label>
                      <Input type="number" value={form.min_payment_percent} onChange={e => setForm({...form, min_payment_percent: e.target.value})} className="mt-1 rounded-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Start Date</Label>
                      <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="mt-1 rounded-none" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">End Date</Label>
                      <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="mt-1 rounded-none" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                      <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active (Now Enrolling)</SelectItem>
                        <SelectItem value="draft">Draft (Hidden)</SelectItem>
                        <SelectItem value="inactive">Inactive (Not In Session)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleCreate} disabled={saving || !form.title} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-programme-btn">
                    {saving ? "Creating..." : "Create Programme"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="h-64 bg-slate-100 animate-pulse" />)}
          </div>
        ) : programmes.length === 0 ? (
          <div className="text-center py-16">
            <GraduationCap size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No programmes available yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programmes.map((prog, idx) => (
              <div key={prog.id} className="relative group/card">
                <Link to={`/programmes/${prog.id}`} data-testid={`programme-card-${idx}`}>
                  <Card className="border border-slate-200 rounded-none shadow-sm hover:border-slate-400 hover:shadow-md transition-all group overflow-hidden">
                    <div className="h-40 overflow-hidden bg-slate-100">
                      <img src={prog.thumbnail || thumbnails[idx % thumbnails.length]} alt={prog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={prog.status === "active" ? "default" : "secondary"} className="rounded-none text-xs capitalize">
                          {prog.status}
                        </Badge>
                        {prog.cohort && <span className="text-xs text-slate-500">{prog.cohort}</span>}
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-blue-800 transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {prog.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{prog.description}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        {prog.duration && <span className="flex items-center gap-1"><Clock size={14} /> {prog.duration}</span>}
                        {prog.fee > 0 && <span className="flex items-center gap-1 font-semibold text-slate-700"><CurrencyDollar size={14} /> ${prog.fee.toLocaleString()}</span>}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                {isAdmin && (
                  <button onClick={(e) => handleDeleteProgramme(prog.id, e)} className="absolute top-2 right-2 bg-white/90 border border-slate-200 p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-600 shadow-sm opacity-0 group-hover/card:opacity-100 transition-opacity z-10" data-testid={`delete-programme-card-${idx}`}>
                    <Trash size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
